// src/modules/system/components/ErrorStatsCards.js
//
// KPI tiles for the System Errors page. Same visual language as the Metrics
// row in modules/time-tracking/TimeTrackingReport.js.
import React from 'react';
import styled from 'styled-components';
import { FaBomb, FaExclamationTriangle, FaMobileAlt, FaServer, FaLayerGroup } from 'react-icons/fa';

const Metrics = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(3)};

  /* auto-fit with a 170px floor drops to a single column on a phone, which
     turns five tiles into a screen and a half of scrolling before you reach
     the actual errors. Force two compact columns instead. */
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${(p) => p.theme.spacing(1)};
    margin-bottom: ${(p) => p.theme.spacing(2)};
  }
`;
const Metric = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; box-shadow: 0 2px 4px rgba(0,0,0,.1);
  .label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small};
    display: flex; align-items: center; gap: 8px; }
  .value { color: ${(p) => p.accent || p.theme.colors.primary}; font-size: 2rem; font-weight: 700; margin-top: 6px; }
  .caption { color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.7rem; margin-top: 2px; }
  svg { color: ${(p) => p.accent || p.theme.colors.primary}; }

  @media (max-width: 900px) {
    padding: ${(p) => p.theme.spacing(1.5)};
    .label { font-size: .68rem; gap: 5px; }
    .value { font-size: 1.4rem; margin-top: 2px; }
    .caption { font-size: .62rem; }
    /* The last tile is odd-one-out in a 2-col grid — let it span the row. */
    &:last-child { grid-column: 1 / -1; }
  }
`;

const countFor = (rows, field, value) => {
  const row = (rows || []).find((r) => r[field] === value);
  return row ? row.count : 0;
};
const occurrencesFor = (rows, field, value) => {
  const row = (rows || []).find((r) => r[field] === value);
  return row ? row.occurrences : 0;
};

const ErrorStatsCards = ({ stats }) => {
  if (!stats) return null;
  const mobileCount = countFor(stats.bySource, 'source', 'mobile');
  const mobileEvents = occurrencesFor(stats.bySource, 'source', 'mobile');
  const apiCount = countFor(stats.bySource, 'source', 'api');

  return (
    <Metrics>
      <Metric accent="#b71c1c">
        <div className="label"><FaBomb /> Crashes</div>
        <div className="value">{stats.fatalCount}</div>
        <div className="caption">app-killing errors</div>
      </Metric>
      <Metric accent="#e74c3c">
        <div className="label"><FaExclamationTriangle /> Unresolved</div>
        <div className="value">{stats.unresolvedCount}</div>
        <div className="caption">{stats.newCount} never triaged</div>
      </Metric>
      <Metric>
        <div className="label"><FaMobileAlt /> Mobile app</div>
        <div className="value">{mobileCount}</div>
        <div className="caption">{mobileEvents} events</div>
      </Metric>
      <Metric>
        <div className="label"><FaServer /> Backend</div>
        <div className="value">{apiCount}</div>
        <div className="caption">distinct errors</div>
      </Metric>
      <Metric>
        <div className="label"><FaLayerGroup /> Total events</div>
        <div className="value">{stats.totalOccurrences}</div>
        <div className="caption">{stats.total} distinct · {stats.windowDays}d</div>
      </Metric>
    </Metrics>
  );
};

export default ErrorStatsCards;
